'use client';

import { useState, type KeyboardEvent, type MouseEvent } from 'react';
import { Portal } from '@ark-ui/react';
import {
  Menu,
  ChangeReasonDialog,
  Checkbox,
  Badge,
  MetaDefault,
  BodyDefault,
  SignalStatusBadge,
  Dialog,
  Tooltip,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  SignalStatus,
  type BlockedReasonType,
  type SignalStageType,
  type SignalStatusType,
} from '@terra/shared-schemas-orchestrator';
import type { FeedbackFailureArea } from '@terra/shared-contracts-signal-feedback';
import { useUserTenants } from '../../hooks/api/useUserTenants';
import { useTranslations } from 'next-intl';
import { SignalFeedbackFailureAreaSelector } from './SignalFeedbackFailureAreaSelector';
import {
  FALSE_POSITIVE_NEW_STATUSES,
  FALSE_POSITIVE_PREVIOUS_STATUSES,
  isFailureAreaStatusChange,
  type SignalRelatedFindingRef,
} from './signalStatusRelatedFinding';
import {
  RELATED_FINDING_CONFIRM_DECISION,
  useSignalStatusUpdate,
  type RelatedFindingConfirmDecision,
} from './useSignalStatusUpdate';

export {
  isFailureAreaStatusChange,
  resolveReportFalsePositive,
  resolveStageForStatusUpdate,
} from './signalStatusRelatedFinding';

const SIGNAL_STATUS_ORDER: readonly SignalStatusType[] = [
  SignalStatus.DISPROVEN_VULNERABILITY,
  SignalStatus.POTENTIAL_VULNERABILITY,
  SignalStatus.CONFIRMED_VULNERABILITY,
  SignalStatus.BLOCKED,
  SignalStatus.DUPLICATE,
];

type SignalStatusSelectorProps = {
  signalId: string;
  currentStage: SignalStageType;
  currentStatus: SignalStatusType | null;
  currentBlockedReason?: BlockedReasonType | null;
  disabled?: boolean;
  relatedFinding?: SignalRelatedFindingRef;
  onOpenRelatedFinding?: (findingId: string) => void;
  onStatusChange?: ({
    stage,
    status,
  }: {
    stage: SignalStageType;
    status: SignalStatusType | null;
  }) => void;
};

export function SignalStatusSelector({
  signalId,
  currentStage,
  currentStatus,
  currentBlockedReason,
  disabled = false,
  relatedFinding,
  onOpenRelatedFinding,
  onStatusChange,
}: SignalStatusSelectorProps) {
  const t = useTranslations('signals.signalStatusSelector');
  const { hasAccessToTerraTenant } = useUserTenants();
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [pendingStatusForUpdate, setPendingStatusForUpdate] = useState<
    SignalStatusType | null | undefined
  >(undefined);
  const [statusChangeNote, setStatusChangeNote] = useState('');
  const [markedAsFalsePositive, setMarkedAsFalsePositive] = useState(false);
  const [failureArea, setFailureArea] = useState<
    FeedbackFailureArea | undefined
  >(undefined);
  const {
    isUpdatingStatus,
    isDeleteConfirmOpen,
    getStatusDisplayText,
    requestStatusUpdate,
    resolveRelatedFindingConfirm,
    buildFalsePositiveFlag,
  } = useSignalStatusUpdate({
    signalId,
    currentStage,
    currentStatus,
    relatedFinding,
    onStatusChange,
  });

  const isFalsePositiveTransition =
    !!currentStatus &&
    !!pendingStatusForUpdate &&
    FALSE_POSITIVE_PREVIOUS_STATUSES.has(currentStatus) &&
    FALSE_POSITIVE_NEW_STATUSES.has(pendingStatusForUpdate);
  const isFailureAreaCaptureTransition =
    pendingStatusForUpdate !== undefined &&
    isFailureAreaStatusChange({
      previousStatus: currentStatus,
      newStatus: pendingStatusForUpdate,
    });
  const isPotentialToDisprovenTransition =
    currentStatus === SignalStatus.POTENTIAL_VULNERABILITY &&
    pendingStatusForUpdate === SignalStatus.DISPROVEN_VULNERABILITY;

  const resetNoteDialogState = () => {
    setPendingStatusForUpdate(undefined);
    setStatusChangeNote('');
    setMarkedAsFalsePositive(false);
    setFailureArea(undefined);
  };

  const handleNoteDialogOpenChange = (open: boolean) => {
    setIsNoteDialogOpen(open);
    if (!open) {
      resetNoteDialogState();
    }
  };

  const openStatusDialog = (newStatus: SignalStatusType | null): void => {
    setPendingStatusForUpdate(newStatus);
    setStatusChangeNote('');
    setMarkedAsFalsePositive(
      currentStatus === SignalStatus.CONFIRMED_VULNERABILITY &&
        newStatus === SignalStatus.DISPROVEN_VULNERABILITY,
    );
    setFailureArea(undefined);
    setIsNoteDialogOpen(true);
  };

  const handleStatusChange = (newStatus: SignalStatusType | null): void => {
    if (disabled || isUpdatingStatus || newStatus === currentStatus) {
      return;
    }

    openStatusDialog(newStatus);
  };

  const handleSaveNote = async () => {
    if (pendingStatusForUpdate === undefined) return;
    if (!canSubmitStatusUpdate) return;

    const result = await requestStatusUpdate({
      newStatus: pendingStatusForUpdate,
      note: statusChangeNote.trim() || undefined,
      isFalsePositive: buildFalsePositiveFlag({
        pendingStatus: pendingStatusForUpdate,
        markedAsFalsePositive,
      }),
      ...(hasAccessToTerraTenant && failureArea ? { failureArea } : {}),
    });

    if (result === 'completed') {
      handleNoteDialogOpenChange(false);
    }
  };

  const handleCancelNote = async () => {
    if (pendingStatusForUpdate === undefined) {
      handleNoteDialogOpenChange(false);

      return;
    }

    const result = await requestStatusUpdate({
      newStatus: pendingStatusForUpdate,
      isFalsePositive: buildFalsePositiveFlag({
        pendingStatus: pendingStatusForUpdate,
        markedAsFalsePositive,
      }),
      ...(hasAccessToTerraTenant && failureArea ? { failureArea } : {}),
    });

    if (result === 'completed') {
      handleNoteDialogOpenChange(false);
    }
  };

  const relatedFindingLabel =
    relatedFinding?.title?.trim() || relatedFinding?.id;

  const settleRelatedFindingConfirm = (
    decision: RelatedFindingConfirmDecision,
  ) => {
    handleNoteDialogOpenChange(false);
    void resolveRelatedFindingConfirm(decision);
  };

  const handleDeleteConfirmOpenChange = (open: boolean) => {
    if (open) {
      return;
    }

    settleRelatedFindingConfirm(RELATED_FINDING_CONFIRM_DECISION.Keep);
  };

  const handleDeleteRelatedFinding = () =>
    settleRelatedFindingConfirm(RELATED_FINDING_CONFIRM_DECISION.Delete);

  const handleKeepRelatedFinding = () =>
    settleRelatedFindingConfirm(RELATED_FINDING_CONFIRM_DECISION.Keep);

  const handleOpenRelatedFindingClick = (
    event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (!relatedFinding) {
      return;
    }

    onOpenRelatedFinding?.(relatedFinding.id);
  };

  const handleOpenRelatedFindingKeyDown = (
    event: KeyboardEvent<HTMLElement>,
  ) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    handleOpenRelatedFindingClick(event);
  };

  const pendingStatusDisplay =
    pendingStatusForUpdate === undefined
      ? null
      : getStatusDisplayText(pendingStatusForUpdate);
  const canSubmitStatusUpdate = isFalsePositiveTransition
    ? statusChangeNote.trim().length > 0 || markedAsFalsePositive
    : statusChangeNote.trim().length > 0;

  const dialogExtraContent = isFailureAreaCaptureTransition ? (
    <VStack gap={2} alignItems="flex-start" width="full">
      {isFalsePositiveTransition && isPotentialToDisprovenTransition ? (
        <HStack gap="2" alignItems="center">
          <Checkbox
            checked={markedAsFalsePositive}
            onCheckedChange={({ checked }) =>
              setMarkedAsFalsePositive(checked === true)
            }
            disabled={isUpdatingStatus}
          >
            {t('reportFalsePositive')}
          </Checkbox>
        </HStack>
      ) : null}
      {hasAccessToTerraTenant ? (
        <SignalFeedbackFailureAreaSelector
          value={failureArea}
          disabled={isUpdatingStatus}
          onValueChange={setFailureArea}
        />
      ) : null}
      {isFalsePositiveTransition ? (
        <MetaDefault color="fg.muted" fontSize="xs">
          {t('falsePositiveExplanation')}
        </MetaDefault>
      ) : null}
    </VStack>
  ) : undefined;

  const unclassifiedBadge = (
    <Badge size="sm" variant="outline">
      {t('unclassified')}
    </Badge>
  );

  const unclassifiedMenuItems =
    currentStatus === null
      ? []
      : [
          {
            value: 'unclassified',
            label: unclassifiedBadge,
            onClick: () => handleStatusChange(null),
            disabled: disabled || isUpdatingStatus,
          },
        ];

  const statusMenuItems = [
    ...unclassifiedMenuItems,
    ...SIGNAL_STATUS_ORDER.filter((status) => currentStatus !== status).map(
      (status) => ({
        value: status,
        label: <SignalStatusBadge status={status} />,
        onClick: () => handleStatusChange(status),
        disabled: disabled || isUpdatingStatus,
      }),
    ),
  ];

  const badgeTrigger =
    currentStatus === null ? (
      <Badge
        size="sm"
        variant="outline"
        cursor={disabled ? 'not-allowed' : 'pointer'}
        pointerEvents={disabled ? 'none' : undefined}
        _hover={disabled ? undefined : { opacity: 0.8 }}
        title={t('changeStatus')}
      >
        {t('unclassified')}
      </Badge>
    ) : (
      <Box
        cursor={disabled ? 'not-allowed' : 'pointer'}
        pointerEvents={disabled ? 'none' : undefined}
        _hover={disabled ? undefined : { opacity: 0.8 }}
        title={t('changeStatus')}
        display="inline-flex"
      >
        <SignalStatusBadge
          status={currentStatus}
          blockedReason={currentBlockedReason}
        />
      </Box>
    );

  return (
    <>
      <Menu.MenuWithItems
        trigger={badgeTrigger}
        items={statusMenuItems}
        header={
          <MetaDefault color="fg.muted">{t('changeStatusTo')}</MetaDefault>
        }
        contentWidth="240px"
        portalled
        positioning={{
          placement: 'bottom-start',
          strategy: 'fixed',
        }}
      />

      <Portal>
        <ChangeReasonDialog
          open={isNoteDialogOpen}
          onOpenChange={handleNoteDialogOpenChange}
          title={t('dialogTitle')}
          description={
            pendingStatusDisplay
              ? t('dialogDescription', { status: pendingStatusDisplay })
              : t('dialogDescriptionFallback')
          }
          selectionLabel={t('dialogSelectionLabel')}
          selection={
            pendingStatusForUpdate !== undefined ? (
              pendingStatusForUpdate === null ? (
                unclassifiedBadge
              ) : (
                <SignalStatusBadge status={pendingStatusForUpdate} />
              )
            ) : undefined
          }
          value={statusChangeNote}
          onValueChange={setStatusChangeNote}
          placeholder={
            isFalsePositiveTransition && isPotentialToDisprovenTransition
              ? t('optionalContextPlaceholder')
              : pendingStatusDisplay
                ? t('dialogPlaceholder', { status: pendingStatusDisplay })
                : t('dialogPlaceholderFallback')
          }
          cancelLabel={t('skip')}
          submitLabel={t('updateStatus')}
          onCancel={handleCancelNote}
          onSubmit={handleSaveNote}
          isSubmitting={isUpdatingStatus}
          isSubmitDisabled={!canSubmitStatusUpdate}
          disableCloseWhileSubmitting={true}
          extraContent={dialogExtraContent}
        />

        <Dialog
          open={isDeleteConfirmOpen}
          onOpenChange={handleDeleteConfirmOpenChange}
          lazyMount
          unmountOnExit
          size="sm"
          title={t('deleteRelatedFindingTitle')}
          description={t('deleteRelatedFindingDescriptionBefore')}
          secondaryAction={{
            label: t('deleteRelatedFindingNo'),
            onClick: handleKeepRelatedFinding,
            disabled: isUpdatingStatus,
          }}
          primaryAction={{
            label: t('deleteRelatedFindingYes'),
            onClick: handleDeleteRelatedFinding,
            loading: isUpdatingStatus,
          }}
        >
          <Box w="full" minW={0}>
            {relatedFindingLabel ? (
              <HStack w="full" minW={0} gap="1" alignItems="baseline">
                <Tooltip
                  title={relatedFindingLabel}
                  positioning={{ strategy: 'fixed' }}
                >
                  <Box
                    minW={0}
                    flex="0 1 auto"
                    overflow="hidden"
                    cursor="default"
                    role="button"
                    tabIndex={0}
                    aria-label={relatedFindingLabel}
                    onClick={handleOpenRelatedFindingClick}
                    onKeyDown={handleOpenRelatedFindingKeyDown}
                  >
                    <BodyDefault truncate>{relatedFindingLabel}</BodyDefault>
                  </Box>
                </Tooltip>
                <BodyDefault flexShrink={0}>
                  {t('deleteRelatedFindingDescriptionAfter')}
                </BodyDefault>
              </HStack>
            ) : null}
          </Box>
        </Dialog>
      </Portal>
    </>
  );
}
